// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Financeiros/AtivosFixos.tsx
//  Ativo Fixo (Imobilizado): cadastro · baixa · processamento de depreciação.
//  Paridade desktop: FrmCadAtivoFixo · FrmBaixaAtivo · FrmProcessarDepreciacao
// ════════════════════════════════════════════════════════════════════════════

import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Plus, Building2, Trash2, Save, TrendingDown, ArchiveX, Eye, Calculator,
} from 'lucide-react';

import { ativoFixoService } from '@/features/financeiro/services/ativoFixoService';
import type {
  AtivoFixoResumo, CriarAtivoFixoRequest, BaixarAtivoFixoRequest,
} from '@/features/financeiro/services/ativoFixoService';
import { SITUACAO_ATIVO_FIXO } from '@/features/financeiro/services/ativoFixoService';
import { getApiErrorMessage } from '@/lib/apiClient';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { PessoaSearchCombobox } from '@/components/PessoaSearchCombobox';

// ── Helpers ────────────────────────────────────────────────────────────────

const brl = (v: number) =>
  (v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/** Formata uma data-only ("yyyy-MM-dd...") como dd/MM/yyyy sem deslocar fuso. */
const fmtData = (s?: string | null) => {
  if (!s) return '—';
  const [y, m, d] = s.slice(0, 10).split('-');
  return d && m && y ? `${d}/${m}/${y}` : s;
};

const hojeIso = () => new Date().toISOString().slice(0, 10);

function situacaoBadge(situacao: number) {
  if (situacao === SITUACAO_ATIVO_FIXO.BAIXADO)
    return <Badge className="bg-red-500/10 text-red-500">Baixado</Badge>;
  if (situacao === SITUACAO_ATIVO_FIXO.TOTALMENTE_DEPRECIADO)
    return <Badge className="bg-yellow-500/10 text-yellow-600">Totalmente Depreciado</Badge>;
  return <Badge className="bg-green-500/10 text-green-500">Ativo</Badge>;
}

// ── Página ───────────────────────────────────────────────────────────────────

export default function AtivosFixos() {
  const queryClient = useQueryClient();

  const [novoOpen, setNovoOpen]           = useState(false);
  const [detalheId, setDetalheId]         = useState<number | null>(null);
  const [baixaAtivo, setBaixaAtivo]       = useState<AtivoFixoResumo | null>(null);
  const [depreciacaoOpen, setDepreciacaoOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['ativos-fixos'],
    queryFn: () => ativoFixoService.getAll(1, 100),
  });

  const ativos = data?.itens ?? [];

  const removeMutation = useMutation({
    mutationFn: (id: number) => ativoFixoService.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ativos-fixos'] });
      toast.success('Ativo excluído');
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const totais = useMemo(() => {
    const aquisicao = ativos.reduce((s, a) => s + a.valorAquisicao, 0);
    const acumulada = ativos.reduce((s, a) => s + a.depreciacaoAcumulada, 0);
    const liquido   = ativos.reduce((s, a) => s + a.valorContabilLiquido, 0);
    return { aquisicao, acumulada, liquido };
  }, [ativos]);

  return (
    <div className="p-6 space-y-4">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Building2 className="h-7 w-7 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Ativo Fixo</h1>
            <p className="text-muted-foreground text-sm">Imobilizado, baixa e depreciação</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" className="gap-2" onClick={() => setDepreciacaoOpen(true)}>
            <Calculator className="h-4 w-4" />Processar Depreciação
          </Button>
          <Button className="gap-2" onClick={() => setNovoOpen(true)}>
            <Plus className="h-4 w-4" />Novo Ativo
          </Button>
        </div>
      </div>

      {/* Totalizadores */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card><CardContent className="pt-4">
          <p className="text-muted-foreground text-sm">Valor de Aquisição</p>
          <p className="text-xl font-bold">{brl(totais.aquisicao)}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <p className="text-muted-foreground text-sm">Depreciação Acumulada</p>
          <p className="text-xl font-bold text-orange-500">{brl(totais.acumulada)}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <p className="text-muted-foreground text-sm">Valor Contábil Líquido</p>
          <p className="text-xl font-bold text-green-600">{brl(totais.liquido)}</p>
        </CardContent></Card>
      </div>

      {/* Lista */}
      <Card>
        <CardHeader><CardTitle className="text-base">Bens Patrimoniais</CardTitle></CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-center py-10 text-muted-foreground">Carregando...</p>
          ) : ativos.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground">
              <Building2 className="h-10 w-10 mx-auto mb-2 opacity-30" />
              <p>Nenhum ativo cadastrado</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Descrição</TableHead>
                  <TableHead>Patrimônio</TableHead>
                  <TableHead>Aquisição</TableHead>
                  <TableHead className="text-right">Valor Aquis.</TableHead>
                  <TableHead className="text-right">Deprec. Acum.</TableHead>
                  <TableHead className="text-right">Valor Líquido</TableHead>
                  <TableHead>Situação</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ativos.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell className="font-medium">{a.descricao}</TableCell>
                    <TableCell>{a.patrimonio || '—'}</TableCell>
                    <TableCell>{fmtData(a.dataAquisicao)}</TableCell>
                    <TableCell className="text-right">{brl(a.valorAquisicao)}</TableCell>
                    <TableCell className="text-right text-orange-500">{brl(a.depreciacaoAcumulada)}</TableCell>
                    <TableCell className="text-right font-medium">{brl(a.valorContabilLiquido)}</TableCell>
                    <TableCell>{situacaoBadge(a.situacao)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" title="Detalhe" onClick={() => setDetalheId(a.id)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        {a.situacao !== SITUACAO_ATIVO_FIXO.BAIXADO && (
                          <Button size="sm" variant="ghost" title="Dar baixa" onClick={() => setBaixaAtivo(a)}>
                            <ArchiveX className="h-4 w-4 text-amber-600" />
                          </Button>
                        )}
                        <Button
                          size="sm" variant="ghost" title="Excluir"
                          onClick={() => {
                            if (confirm(`Excluir o ativo "${a.descricao}"?`)) removeMutation.mutate(a.id);
                          }}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {novoOpen && <NovoAtivoDialog onClose={() => setNovoOpen(false)} />}
      {detalheId !== null && <DetalheDialog id={detalheId} onClose={() => setDetalheId(null)} />}
      {baixaAtivo && <BaixaDialog ativo={baixaAtivo} onClose={() => setBaixaAtivo(null)} />}
      {depreciacaoOpen && <DepreciacaoDialog onClose={() => setDepreciacaoOpen(false)} />}
    </div>
  );
}

// ── Dialog: Novo Ativo ───────────────────────────────────────────────────────

function NovoAtivoDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();

  const [descricao, setDescricao]       = useState('');
  const [patrimonio, setPatrimonio]     = useState('');
  const [dataAquisicao, setDataAquisicao] = useState(hojeIso());
  const [valorAquisicao, setValorAquisicao] = useState(0);
  const [valorResidual, setValorResidual] = useState(0);
  const [vidaUtilMeses, setVidaUtilMeses] = useState(60);
  const [fornecedorId, setFornecedorId] = useState<number | undefined>(undefined);
  const [numeroNota, setNumeroNota]     = useState('');
  const [localizacao, setLocalizacao]   = useState('');
  const [observacao, setObservacao]     = useState('');

  const depMensal = vidaUtilMeses > 0
    ? Math.round(((valorAquisicao - valorResidual) / vidaUtilMeses) * 100) / 100
    : 0;

  const saveMutation = useMutation({
    mutationFn: () => {
      const req: CriarAtivoFixoRequest = {
        descricao,
        dataAquisicao: new Date(dataAquisicao).toISOString(),
        valorAquisicao,
        vidaUtilMeses,
        valorResidual,
        patrimonio: patrimonio || undefined,
        fornecedorId: fornecedorId || undefined,
        numeroNota: numeroNota || undefined,
        localizacao: localizacao || undefined,
        observacao: observacao || undefined,
      };
      return ativoFixoService.create(req);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ativos-fixos'] });
      toast.success('Ativo cadastrado');
      onClose();
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Novo Ativo Imobilizado</DialogTitle>
          <DialogDescription>Cadastre um bem patrimonial e seus parâmetros de depreciação.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
          <div className="space-y-2 md:col-span-2">
            <Label>Descrição *</Label>
            <Input value={descricao} onChange={(e) => setDescricao(e.target.value)} placeholder="Ex.: Notebook Dell Latitude" />
          </div>
          <div className="space-y-2">
            <Label>Patrimônio / Plaqueta</Label>
            <Input value={patrimonio} onChange={(e) => setPatrimonio(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Data de Aquisição</Label>
            <Input type="date" value={dataAquisicao} onChange={(e) => setDataAquisicao(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Valor de Aquisição (R$)</Label>
            <Input type="number" step="0.01" value={valorAquisicao}
              onChange={(e) => setValorAquisicao(Number(e.target.value))} />
          </div>
          <div className="space-y-2">
            <Label>Valor Residual (R$)</Label>
            <Input type="number" step="0.01" value={valorResidual}
              onChange={(e) => setValorResidual(Number(e.target.value))} />
          </div>
          <div className="space-y-2">
            <Label>Vida Útil (meses)</Label>
            <Input type="number" value={vidaUtilMeses}
              onChange={(e) => setVidaUtilMeses(Number(e.target.value))} />
          </div>
          <div className="space-y-2">
            <Label>Depreciação Mensal (calc.)</Label>
            <Input value={brl(depMensal)} disabled className="bg-muted/40" />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label>Fornecedor</Label>
            <PessoaSearchCombobox value={fornecedorId} onChange={setFornecedorId}
              filter="fornecedor" placeholder="Selecione o fornecedor..." />
          </div>
          <div className="space-y-2">
            <Label>Nº Nota Fiscal</Label>
            <Input value={numeroNota} onChange={(e) => setNumeroNota(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Localização</Label>
            <Input value={localizacao} onChange={(e) => setLocalizacao(e.target.value)} />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label>Observações</Label>
            <Textarea value={observacao} onChange={(e) => setObservacao(e.target.value)} rows={2} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button className="gap-2" disabled={saveMutation.isPending} onClick={() => saveMutation.mutate()}>
            <Save className="h-4 w-4" />Cadastrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Dialog: Detalhe + histórico de depreciação ──────────────────────────────

function DetalheDialog({ id, onClose }: { id: number; onClose: () => void }) {
  const { data: ativo, isLoading } = useQuery({
    queryKey: ['ativo-fixo', id],
    queryFn: () => ativoFixoService.getById(id),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{ativo?.descricao ?? 'Ativo'}</DialogTitle>
          <DialogDescription>Detalhe do bem e histórico de depreciação.</DialogDescription>
        </DialogHeader>

        {isLoading || !ativo ? (
          <p className="text-center py-8 text-muted-foreground">Carregando...</p>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div><p className="text-muted-foreground">Situação</p>{situacaoBadge(ativo.situacao)}</div>
              <div><p className="text-muted-foreground">Aquisição</p><p className="font-medium">{fmtData(ativo.dataAquisicao)}</p></div>
              <div><p className="text-muted-foreground">Início Depreciação</p><p className="font-medium">{fmtData(ativo.dataInicioDepreciacao)}</p></div>
              <div><p className="text-muted-foreground">Vida Útil</p><p className="font-medium">{ativo.vidaUtilMeses} meses</p></div>
              <div><p className="text-muted-foreground">Valor Aquisição</p><p className="font-bold">{brl(ativo.valorAquisicao)}</p></div>
              <div><p className="text-muted-foreground">Valor Residual</p><p className="font-medium">{brl(ativo.valorResidual)}</p></div>
              <div><p className="text-muted-foreground">Deprec. Mensal</p><p className="font-medium">{brl(ativo.depreciacaoMensal)}</p></div>
              <div><p className="text-muted-foreground">Deprec. Acumulada</p><p className="font-medium text-orange-500">{brl(ativo.depreciacaoAcumulada)}</p></div>
              <div><p className="text-muted-foreground">Valor Líquido</p><p className="font-bold text-green-600">{brl(ativo.valorContabilLiquido)}</p></div>
              {ativo.localizacao && <div><p className="text-muted-foreground">Localização</p><p className="font-medium">{ativo.localizacao}</p></div>}
              {ativo.numeroNota && <div><p className="text-muted-foreground">Nº Nota</p><p className="font-medium">{ativo.numeroNota}</p></div>}
            </div>

            {ativo.situacao === SITUACAO_ATIVO_FIXO.BAIXADO && (
              <Card><CardContent className="pt-4 text-sm">
                <p className="font-medium text-red-500">Ativo baixado em {fmtData(ativo.dataBaixa)}</p>
                {ativo.valorBaixa != null && <p className="text-muted-foreground">Valor de baixa: {brl(ativo.valorBaixa)}</p>}
                {ativo.motivoBaixa && <p className="text-muted-foreground">Motivo: {ativo.motivoBaixa}</p>}
              </CardContent></Card>
            )}

            <div>
              <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
                <TrendingDown className="h-4 w-4" />Histórico de Depreciação
              </h3>
              {ativo.depreciacoes.length === 0 ? (
                <p className="text-sm text-muted-foreground py-3">Nenhuma cota processada.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Competência</TableHead>
                      <TableHead className="text-right">Valor</TableHead>
                      <TableHead className="text-right">Acumulado</TableHead>
                      <TableHead className="text-right">Valor Líquido</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ativo.depreciacoes.map((d) => (
                      <TableRow key={d.id}>
                        <TableCell>{fmtData(d.competencia).slice(3)}</TableCell>
                        <TableCell className="text-right">{brl(d.valor)}</TableCell>
                        <TableCell className="text-right">{brl(d.valorAcumulado)}</TableCell>
                        <TableCell className="text-right">{brl(d.valorContabilLiquido)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </div>
        )}

        <DialogFooter><Button variant="outline" onClick={onClose}>Fechar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Dialog: Baixa ────────────────────────────────────────────────────────────

function BaixaDialog({ ativo, onClose }: { ativo: AtivoFixoResumo; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [dataBaixa, setDataBaixa] = useState(hojeIso());
  const [motivo, setMotivo]       = useState('');
  const [valorBaixa, setValorBaixa] = useState<number | ''>('');

  const baixaMutation = useMutation({
    mutationFn: () => {
      const req: BaixarAtivoFixoRequest = {
        dataBaixa: new Date(dataBaixa).toISOString(),
        motivo,
        valorBaixa: valorBaixa === '' ? undefined : Number(valorBaixa),
      };
      return ativoFixoService.baixar(ativo.id, req);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ativos-fixos'] });
      toast.success('Ativo baixado');
      onClose();
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Dar Baixa — {ativo.descricao}</DialogTitle>
          <DialogDescription>Valor líquido atual: {brl(ativo.valorContabilLiquido)}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Data da Baixa</Label>
            <Input type="date" value={dataBaixa} onChange={(e) => setDataBaixa(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Valor de Baixa / Venda (R$)</Label>
            <Input type="number" step="0.01" value={valorBaixa}
              onChange={(e) => setValorBaixa(e.target.value === '' ? '' : Number(e.target.value))}
              placeholder="Opcional" />
          </div>
          <div className="space-y-2">
            <Label>Motivo *</Label>
            <Textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={2}
              placeholder="Ex.: Venda, sucateamento, doação..." />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button variant="destructive" className="gap-2"
            disabled={baixaMutation.isPending || !motivo.trim()}
            onClick={() => baixaMutation.mutate()}>
            <ArchiveX className="h-4 w-4" />Confirmar Baixa
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Dialog: Processar Depreciação ────────────────────────────────────────────

function DepreciacaoDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const now = new Date();
  const [ano, setAno] = useState(now.getFullYear());
  const [mes, setMes] = useState(now.getMonth() + 1);

  const processarMutation = useMutation({
    mutationFn: () => ativoFixoService.processarDepreciacao({ ano, mes }),
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: ['ativos-fixos'] });
      toast.success(
        `Depreciação processada: ${r.ativosProcessados} ativo(s), ${brl(r.totalDepreciado)}` +
        (r.ativosIgnorados ? ` (${r.ativosIgnorados} ignorado(s))` : ''),
      );
      onClose();
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Processar Depreciação</DialogTitle>
          <DialogDescription>
            Gera a cota de depreciação de todos os ativos elegíveis na competência.
            Ativos já processados nesse mês são ignorados.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 py-2">
          <div className="space-y-2">
            <Label>Mês</Label>
            <Input type="number" min={1} max={12} value={mes}
              onChange={(e) => setMes(Number(e.target.value))} />
          </div>
          <div className="space-y-2">
            <Label>Ano</Label>
            <Input type="number" min={2000} max={2100} value={ano}
              onChange={(e) => setAno(Number(e.target.value))} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button className="gap-2" disabled={processarMutation.isPending}
            onClick={() => processarMutation.mutate()}>
            <Calculator className="h-4 w-4" />Processar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
